import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { titleCase } from "../lib/format";
import type { Admin, InviteInfo } from "../lib/types";
import { PasswordFields } from "./PasswordFields";
import { passwordsAcceptable } from "./password";

/**
 * ── /invite/:token — the only way into this console ────────────────────────
 * Public, and mounted OUTSIDE the auth gate: the whole point is that the
 * person opening it has no account yet.
 *
 * The email, the name, the mobile and the role are read-only, because they
 * come off the invite row rather than off this form. The server ignores them
 * if they are posted anyway — otherwise an invitation to be an analyst would
 * be an invitation to be whatever the invitee cared to type.
 *
 * The texted code is not ceremony. The OWNER typed that mobile number, and a
 * mistyped digit would otherwise produce an account that is created perfectly
 * and can never sign in again — every future sign-in would text a code to
 * somebody else's phone, and only shell access could fix it. Proving the
 * number works before the account exists turns that into a problem the
 * invitee notices in ten seconds.
 */
export function InvitePage({ onAccepted }: { onAccepted: (a: Admin) => void }) {
  const { token = "" } = useParams();

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The invitation states both of these in plain sight, so neither may appear
  // in the password it is used to choose.
  const identity = invite
    ? { email: invite.email, fullName: invite.fullName }
    : {};

  useEffect(() => {
    let cancelled = false;

    api<{ invite: InviteInfo }>(
      `/api/admin/auth/invite/${encodeURIComponent(token)}`,
    )
      .then((res) => {
        if (!cancelled) setInvite(res.invite);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error
              ? err.message
              : "This invitation link is not valid any more.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function sendCode() {
    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      await api(`/api/admin/auth/invite/${encodeURIComponent(token)}/send-code`, {
        method: "POST",
      });
      setCodeSent(true);
      setNotice("Code sent. It expires in 10 minutes.");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't send the code.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    // Checked here for the instant feedback, and again on the server — a
    // mismatch only the client catches is a password nobody knows the moment
    // the client has a bug.
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }

    // The button is disabled until this holds, so reaching it means the form
    // was submitted some other way — Enter on an autofilled field, mostly.
    // The rules are the account's only lock; they don't get skipped.
    if (!passwordsAcceptable(password, confirm, identity)) {
      setError("That password doesn't meet the requirements below yet.");
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      const res = await api<{ token: string; admin: Admin }>(
        `/api/admin/auth/invite/${encodeURIComponent(token)}/accept`,
        {
          method: "POST",
          body: { password, confirmPassword: confirm, code },
        },
      );
      setToken(res.token);
      // Straight into the console — between the link and the texted code they
      // have already proved both factors, and making someone who has just
      // chosen a password type it again is ceremony.
      onAccepted(res.admin);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="login">
        <p className="empty">Checking your invitation…</p>
      </div>
    );
  }

  if (loadError || !invite) {
    return (
      <div className="login">
        <div className="login-card">
          <div className="brand-mark" />
          <h1>This link has expired</h1>
          <p className="sub">{loadError}</p>
          <p className="field-hint">
            Invitations last seven days and work once. If yours has already
            been used, you have an account — sign in at{" "}
            <a href="/" style={{ textDecoration: "underline" }}>
              the console
            </a>{" "}
            instead.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <div className="brand-mark" />
        <h1>Set up your account</h1>
        <p className="sub">
          {invite.invitedByEmail} invited you to the JK Maxx admin console.
        </p>

        {/* Read-only, and visibly so. These are facts about the invitation,
            not fields — showing them as inputs would imply they can be
            changed, and the server would ignore it if they were. */}
        <div
          className="detail-grid"
          style={{
            marginBottom: 18,
            padding: "12px 14px",
            background: "var(--surface-2)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <div className="detail-item">
            <div className="k">Signing in as</div>
            <div className="v mono">{invite.email}</div>
          </div>
          <div className="detail-item">
            <div className="k">Your access</div>
            <div className="v">{titleCase(invite.role)}</div>
          </div>
          <div className="detail-item">
            <div className="k">Sign-in codes go to</div>
            <div className="v mono">{invite.phoneHint}</div>
          </div>
        </div>

        {error && <p className="form-error">{error}</p>}
        {notice && (
          <p className="field-hint" style={{ marginBottom: 12 }}>
            {notice}
          </p>
        )}

        {/* Hidden but present: without it a password manager saves the
            credential against no account and cannot offer it back later. */}
        <input
          type="email"
          value={invite.email}
          autoComplete="username"
          readOnly
          hidden
        />

        <PasswordFields
          label="Choose a password"
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          identity={identity}
          autoFocus
        />

        <div className="field">
          <label htmlFor="invite-code">
            Code texted to {invite.phoneHint}
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              id="invite-code"
              className="input mono"
              style={{ letterSpacing: "0.2em", flex: 1 }}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              required
            />
            <button
              type="button"
              className="btn"
              onClick={() => void sendCode()}
              disabled={busy}
            >
              {codeSent ? "Resend" : "Send code"}
            </button>
          </div>
          <span className="field-hint">
            {codeSent
              ? "Not the right number? Ask for a new invitation — it can't be changed here."
              : "This confirms the number is really yours before the account is created."}
          </span>
        </div>

        <button
          className="btn btn-primary"
          style={{ width: "100%", marginTop: 6 }}
          disabled={
            busy ||
            code.length < 6 ||
            !passwordsAcceptable(password, confirm, identity)
          }
        >
          {busy ? "Setting up…" : "Create account & sign in"}
        </button>

        <div className="login-switch">
          Nobody else sees this password — not even the person who invited you.
        </div>
      </form>
    </div>
  );
}
